// Staff landing screen. Two large primary actions: Stamp (opens the
// scanner) and Enroll (opens the customer hand-off form). Replaces the
// scanner-as-index default so opening /staff doesn't grab the camera
// before the staff member has decided what they want to do.

import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { pickGreeting } from '../../shared/greetings.js'
import { useStaffSession } from '../hooks/useStaffSession.js'
import {
  StaffStampOverlay,
  type StaffStampSource,
} from '../components/StaffStampOverlay.js'
import '../../shared/redemption/redemptionUI.css'
import '../../owner/pages/stampPage.css'
import './staff.css'

interface StaffHomeLocationState {
  stampSource?: StaffStampSource
}

export function StaffHomePage() {
  const { data: me } = useStaffSession()
  const location = useLocation()
  const navigate = useNavigate()
  const name = me?.user.display_name?.split(' ')[0]?.trim() || 'there'
  // One greeting per mount — fresh pick on reload/navigation back, stable
  // while the home is open. See pickGreeting for the 18-message pool.
  const greeting = useMemo(() => pickGreeting(name), [name])

  const [stampSource, setStampSource] = useState<StaffStampSource | null>(null)
  useEffect(() => {
    const state = location.state as StaffHomeLocationState | null
    if (state?.stampSource) {
      setStampSource(state.stampSource)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate])

  return (
    <div className="staff-home">
      <h1>{greeting}</h1>
      <p className="stamp-hint">What are you doing right now?</p>

      <div className="staff-home-actions">
        <Link to="/staff/scan" className="staff-home-card staff-home-stamp">
          <StampGlyph />
          <span className="staff-home-card-title">Stamp a customer</span>
          <span className="staff-home-card-sub">
            Scan their loyalty pass QR
          </span>
        </Link>

        <Link to="/staff/enroll" className="staff-home-card staff-home-enroll">
          <EnrollGlyph />
          <span className="staff-home-card-title">Enroll a new customer</span>
          <span className="staff-home-card-sub">
            Collect their details, hand off a QR
          </span>
        </Link>
      </div>

      {stampSource && (
        <StaffStampOverlay
          source={stampSource}
          onDone={() => setStampSource(null)}
        />
      )}
    </div>
  )
}

function StampGlyph() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M8 12l3 3 5-5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function EnrollGlyph() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="3"
        width="7"
        height="7"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <rect
        x="14"
        y="3"
        width="7"
        height="7"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <rect
        x="3"
        y="14"
        width="7"
        height="7"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M14 14h4M14 18h4M14 21h2M21 14v7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}
